import { ArrowLeft, Play, Plus, Stethoscope } from "lucide-react";
import type { ReactNode } from "react";

import type { Patient } from "@elation/api-client/src/stitch-web/models";

import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";

import { PatientEntity } from "../../../../domain";

export type PatientDetailVisitActionV2 = "start" | "resume";

export interface PatientDetailHeaderV2Props {
  patient?: Patient | null;
  visitAction?: PatientDetailVisitActionV2 | null;
  isVisitActionPending?: boolean;
  trailingActions?: ReactNode;
  onBack: () => void;
  onVisitAction: () => void;
}

export function PatientDetailHeaderV2({
  patient,
  visitAction = "start",
  isVisitActionPending = false,
  trailingActions,
  onBack,
  onVisitAction,
}: PatientDetailHeaderV2Props) {
  const patientName = PatientEntity.displayName(patient) ?? "Unknown patient";
  const patientLine = PatientEntity.patientLine(patient);
  const isResume = visitAction === "resume";
  const actionLabel = isVisitActionPending
    ? isResume
      ? "Resuming…"
      : "Starting…"
    : isResume
      ? "Resume"
      : "Start a visit";

  return (
    <HorizontalFlexbox
      alignItems="center"
      justifyContent="space-between"
      className="gap-3 border-b border-border px-[18px] py-3.5 md-stitch:px-6"
    >
      <HorizontalFlexbox alignItems="center" className="min-w-0 gap-2.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 gap-1.5 px-2 text-[12.5px] font-medium text-muted-foreground"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back
        </Button>
        <VerticalFlexbox className="min-w-0 gap-0.5">
          <h1 className="truncate text-[13.5px] font-semibold tracking-[-0.01em] text-surface-foreground">
            {patientName}
          </h1>
          {patientLine ? (
            <p className="font-mono text-[10.5px] text-muted-foreground">
              {patientLine}
            </p>
          ) : null}
        </VerticalFlexbox>
      </HorizontalFlexbox>

      <HorizontalFlexbox alignItems="center" className="shrink-0 gap-1.5">
        {trailingActions}
        {visitAction ? (
          <Button
            type="button"
            className="h-8 shrink-0 rounded-[9px] px-3 text-[13px] font-semibold"
            disabled={isVisitActionPending}
            aria-busy={isVisitActionPending}
            onClick={onVisitAction}
          >
            {isVisitActionPending ? (
              <Stethoscope
                className="size-4 animate-pulse"
                aria-hidden="true"
              />
            ) : isResume ? (
              <Play className="size-4" aria-hidden="true" />
            ) : (
              <Plus className="size-4" aria-hidden="true" />
            )}
            {actionLabel}
          </Button>
        ) : null}
      </HorizontalFlexbox>
    </HorizontalFlexbox>
  );
}
